<template>
  <div
    class="specialdays-timeline"
    v-resize="onResize"
  >
    <svg
      ref="view"
      :viewBox="`0 0 ${width} ${height}`"
      :width="`${width}px`" :height="`${height}px`"
    >
      <defs>
        <filter id="shadow" x="0" y="0" width="150%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.8" result="DROP"/>
          <feFlood flood-color="#000" flood-opacity="0.4" result="COLOR"/>
          <feComposite in="COLOR" in2="DROP" operator="in" result="SHADOW"/>
          <feOffset in="SHADOW" dx="1" dy="1" result="DROPSHADOW"/>
          <feMerge>
            <feMergeNode in="DROPSHADOW"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <text
        x="8.5" y="16"
        font-size="12"
      >{{month}}</text>

      <rect
        x="0" y="21"
        :width="width" :height="height ? height - 22 : 0"
        class="box"
      />

      <line
        x1="0" y1="21.5"
        :x2="width" y2="21.5"
        class="hairline"
      />
      <line
        x1="0" y1="40.5"
        :x2="width" y2="40.5"
        class="hairline"
      />
      <polygon
        :points="reservedPolygon(period)"
        class="reserved"
      />

      <g
        v-for="(item, index) in dates"
        :key="index"
      >
        <text
          v-if="index > 0"
          :x="(index + .5) * dayWidth - 7"
          y="16"
          font-size="12"
        >{{item.date | monthText}}</text>

        <rect
          :x="index * dayWidth + 0.5" y="21.5"
          :width="dayWidth" :height="19"
          class="date-cell"
          :class="cellClass(item)"
        />

        <text
          :x="(index + .5) * dayWidth"
          y="36"
          class="date-text"
          :class="cellClass(item)"
        >{{item.date | dayText}}</text>

        <line
          :x1="(index + 1) * dayWidth + 0.5"
          :x2="(index + 1) * dayWidth + 0.5"
          y1="21.5"
          :y2="height"
          class="hairline"
        />
      </g>

      <!-- 利用可能範囲を描画 -->
      <polygon
        v-for="(item, index) in allows"
        :key="`${car.id}:f:${index}`"
        :points="allowPolygon(item)"
        class="allow"
        filter="url(#shadow)"
        @click.capture.prevent="onSelected($event, item)"
      />

      <line
        x1="0" :y1="height - .5"
        :x2="width" :y2="height - .5"
        class="hairline"
      />
    </svg>
  </div>
</template>

<style lang="scss">
@import '@/libs/vuetify/styles/variables.scss';

.specialdays-timeline {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 72px;
  overflow: hidden;

  > svg {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  .box {
    fill: #fdfdfd;
  }

  .hairline {
    stroke: #ccc;
    stroke-width: 1;
  }

  .date-cell {
    fill: transparent;
  }

  .date-cell.is-closed {
    fill: #66BB6A;
  }

  .date-text {
    text-anchor: middle;
    font-size: 10px;
    font-weight: bold;
  }

  .date-text.is-saturday {
    fill: #2962FF !important;
  }

  .date-text.is-holiday {
    fill: #F44336 !important;
  }

  .date-text.is-closed {
    fill: white;
  }

  .reserved {
    stroke-width: 0;
    fill: #ddd;
  }

  .allow {
    stroke-width: 0;
    fill: #1976D2;
    cursor: pointer;
  }
}
</style>

<script>
import * as time from '@/libs/time'
import * as C from '@/app/special/consts'

export default {
  name: 'DaysTimeline',
  props: {
    period: {
      type: Object,
      required: true
    },
    dates: {
      type: Array,
      required: true
    },
    car: {
      type: Object,
      required: true
    },
    allows: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      width: 0,
      height: 0
    }
  },
  computed: {
    firstDate () {
      if (!this.dates || this.dates.length === 0) {
        return time.today()
      }
      return time.make(this.dates[0].date)
    },
    month () {
      return time.format('n 月', this.firstDate)
    },
    dayWidth () {
      return Math.floor(this.width / C.TIMELINE_DAYS)
    },
    timelineY () {
      return (this.height - 40) * 0.5 + 40
    }
  },
  filters: {
    //
    // 日にちの文字列を返す
    //
    dayText (tm) {
      return time.format('j', tm)
    },

    monthText (tm) {
      const d = time.make(tm)
      return (d.getDate() === 1) ? time.format('n 月', d) : ''
    }
  },
  created () {
    const timerid = setInterval(() => {
      const el = this.$refs.view
      if (!el || el.clientHeight === 0) {
        return
      }

      clearInterval(timerid)
      this.onResize()
    }, 100)
  },
  methods: {
    //
    // リサイズ時、要素のサイズを取得する
    //
    onResize () {
      const el = this.$refs.view
      this.width = el.clientWidth
      this.height = el.clientHeight
    },

    //
    // 利用可能 Polygon のクリックイベント
    //
    onSelected (ev, allow) {
      // svg の座標系を得る
      const el = this.$refs.view
      const box = el.getBoundingClientRect()

      // svg 座標から日付を得る
      const d = this.xday(ev.clientX - box.left)
      const date = time.format('Y-m-d', d)

      // 日付の定義を得る
      const item = this.dates.find(item => item.date === date)
      if (!item) {
        return
      }
      if (item.closed) {
        // 定休日は反応しない
        return
      }

      // period (その日の利用可能期間) を得る
      const period = {
        begin: time.join(date, item.begin),
        end: time.join(date, item.end)
      }
      if (period.begin < allow.begin) {
        period.begin = allow.begin
      }
      if (period.end > allow.end) {
        period.end = allow.end
      }

      // selected を emit する
      this.$emit('selected', {
        carid: this.car.id,
        period,
        allow
      })
    },

    //
    // X 座標から日付を返す
    //
    xday (x) {
      const offset = Math.floor(x / this.dayWidth)
      return new Date(this.firstDate.valueOf() + (offset * time.DAY))
    },

    //
    // 日時から X 座標を返す
    //
    xaxis (tm) {
      const f = this.firstDate
      const t = tm
      const diff = (t.valueOf() - f.valueOf()) / time.DAY
      return Math.round(diff * this.dayWidth)
    },

    //
    // 期間を表すポリゴンを返す
    //
    reservedPolygon (period) {
      const top = this.timelineY - 8 + 0.5
      const bottom = this.timelineY + 8 + 0.5
      const first = this.xaxis(period.begin)
      const last = this.xaxis(period.end)

      const points = [
        `${first} ${top}`,
        `${last} ${top}`,
        `${last} ${bottom}`,
        `${first} ${bottom}`
      ]
      return points.join(' ')
    },
    allowPolygon (period) {
      const top = this.timelineY - 10 + 0.5
      const bottom = this.timelineY + 10 + 0.5
      const first = this.xaxis(period.begin)
      const last = this.xaxis(period.end)

      const points = [
        `${first + 2} ${top}`,
        `${last + 2} ${top}`,
        `${last - 2} ${bottom}`,
        `${first - 2} ${bottom}`
      ]
      return points.join(' ')
    },

    cellClass (item) {
      const classes = []
      if (item.closed) {
        classes.push('is-closed')
      }
      if (item.holiday) {
        classes.push('is-holiday')
      } else if (item.saturday) {
        classes.push('is-saturday')
      }
      return classes
    }
  }
}
</script>
